import { useQueryClient } from "@tanstack/react-query";
import React, { useCallback } from "react";

import { Chip, SingleSelectInput } from "@performyard/py-components";

import { reviewFormsQuery } from "../../../api/queries";

export type IReviewForm = {
	_id: string;
	name: string;
	deleted_at?: string;
};

const FormOptionLabel = ({ form, showDeleted = true }: { form: IReviewForm; showDeleted?: boolean }) => (
	<span className="flex items-center gap-2 text-paragraph-3">
		<span className="min-w-0 flex-1 truncate">{form?.name}</span>
		{showDeleted && form?.deleted_at && <Chip label="Deleted" color="red" size={20} />}
	</span>
);

interface ReviewFormsSelectProps {
	/**
	 * current value of the form picker
	 */
	value?: IReviewForm;

	/**
	 * function runs on form picker change
	 */
	onChange?: (value: IReviewForm | null) => void;

	/**
	 * label for the form picker
	 */
	label?: string;

	/**
	 * if true, the form picker will have a clear button
	 */
	clearable?: boolean;

	/**
	 * placeholder for the form picker
	 */
	placeholder?: string;

	/**
	 * if true, the form picker will include deleted forms
	 */
	includeDeleted?: boolean;

	/**
	 * if true, the form picker will include used forms
	 */
	includeUsed?: boolean;

	/**
	 * if true, group forms by name and show unique names only (for filtering by name)
	 */
	groupByName?: boolean;
}

const ReviewFormsSelect = ({
	value,
	onChange,
	clearable = false,
	placeholder = "Enter form name...",
	includeDeleted = false,
	includeUsed = false,
	groupByName = false,
}: ReviewFormsSelectProps) => {
	const queryClient = useQueryClient();

	// Use TanStack Query with fetchOptions to get both caching AND debouncing
	// SingleSelectInput handles debouncing, TanStack Query handles caching across instances
	const fetchOptions = useCallback(
		async (searchTerm: string) => {
			// Use ensureQueryData to leverage cache or fetch if not cached
			// This provides instant results from cache when available
			const results = await queryClient.ensureQueryData(
				reviewFormsQuery({
					searchTerm,
					includeDeleted,
					includeUsed,
				})
			);

			// If groupByName is true, group forms by name and show unique names only
			// This is used for filtering where we want to search by name, not by specific form ID
			if (groupByName) {
				const uniqueFormsByName = new Map<string, IReviewForm>();
				results?.results?.forEach((form) => {
					const formName = form?.name;
					if (!formName) return;

					const existingForm = uniqueFormsByName.get(formName);
					const currentForm: IReviewForm = {
						_id: form?._id,
						name: formName,
						deleted_at: form?.deleted_at,
					} as IReviewForm;

					// If no form with this name exists yet, add it
					if (!existingForm) {
						uniqueFormsByName.set(formName, currentForm);
					} else {
						// If we already have a form with this name, prefer the non-deleted one
						const existingIsDeleted = !!existingForm.deleted_at;
						const currentIsDeleted = !!currentForm.deleted_at;

						// Replace if: existing is deleted but current is not
						if (existingIsDeleted && !currentIsDeleted) {
							uniqueFormsByName.set(formName, currentForm);
						}
						// If both have same deleted status, keep the existing one (first encountered)
					}
				});

				return Array.from(uniqueFormsByName.values()).map((form) => ({
					label: <FormOptionLabel form={form} showDeleted={false} />,
					value: form,
				}));
			}

			// Default behavior: show all forms without grouping
			return results?.results?.map((form) => ({
				label: <FormOptionLabel form={form} />,
				value: { _id: form?._id, name: form?.name, deleted_at: form?.deleted_at } as IReviewForm,
			}));
		},
		[queryClient, includeDeleted, includeUsed, groupByName]
	);

	const renderValue = useCallback(
		(formValue: IReviewForm) => <FormOptionLabel form={formValue} showDeleted={!groupByName} />,
		[groupByName]
	);

	return (
		<SingleSelectInput<IReviewForm>
			fetchOptions={fetchOptions}
			onChange={(newFormValue) => {
				if (onChange) {
					onChange(newFormValue);
				}
			}}
			value={value || undefined}
			isClearable={clearable}
			selectionVariant="combobox"
			placeholder={placeholder}
			renderValue={renderValue}
		/>
	);
};

export default ReviewFormsSelect;
